import { useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { LOCALES, type Locale } from '@rozman/shared';
import { applyLocale } from '@/lib/i18n';
import type { JSX } from 'react';

/**
 * Present on every page including checkout. The choice persists across reloads and is
 * reflected in the URL, so a Ukrainian page is linkable and indexable.
 */
export function LanguageSwitcher({ compact = false }: { compact?: boolean }): JSX.Element {
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const current = i18n.language as Locale;

  const switchTo = (locale: Locale): void => {
    if (locale === current) return;
    applyLocale(locale);
    const rest = location.pathname.replace(/^\/(pl|uk)/, '');
    navigate(`/${locale}${rest}${location.search}`, { replace: false });
  };

  return (
    <div
      className="inline-flex items-center gap-0.5"
      role="group"
      aria-label={t('common.language')}
      data-testid="language-switcher"
    >
      {LOCALES.map((locale) => (
        <button
          key={locale}
          type="button"
          onClick={() => switchTo(locale)}
          aria-current={locale === current ? 'true' : undefined}
          lang={locale}
          className="inline-flex min-h-11 min-w-8 items-center justify-center px-1 text-2xs font-semibold uppercase tracking-widest transition-colors duration-200 sm:min-w-9 sm:px-2"
          style={{
            color: locale === current ? 'var(--color-ink)' : 'var(--color-muted)',
            textDecoration: locale === current ? 'underline' : 'none',
            textUnderlineOffset: '4px',
          }}
        >
          {compact ? locale : locale === 'pl' ? t('common.polish') : t('common.ukrainian')}
        </button>
      ))}
    </div>
  );
}
